import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import api from "../lib/api";

export default function PaymentResult({ cancelled }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");

  useEffect(() => {
    if (cancelled) return;
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
      } catch { /* keep polling */ }
      if (stop) return;
      if (++tries > 10) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [cancelled, params]);

  const C = {
    checking: { icon: <Loader2 size={40} className="animate-spin text-brand-purple" />, title: "Confirming your payment…", sub: "Hang tight, this only takes a moment." },
    paid: { icon: <CheckCircle2 size={40} className="text-green-600" />, title: "Payment successful!", sub: "Thank you — we've received your payment and emailed you a receipt." },
    pending: { icon: <Clock size={40} className="text-brand-orange" />, title: "Payment processing", sub: "Your payment is still processing. We'll confirm by email shortly." },
    cancelled: { icon: <XCircle size={40} className="text-brand-purpleDeep/40" />, title: "Payment cancelled", sub: "No charge was made. You can pay your quote any time from your dashboard." },
    error: { icon: <XCircle size={40} className="text-red-500" />, title: "Something went wrong", sub: "We couldn't find that payment session." },
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFC] px-5" data-testid="payment-result">
      <div className="max-w-md text-center bg-white rounded-2xl border border-border p-10 shadow-[0_10px_40px_rgba(130,26,200,0.06)]">
        <div className="mx-auto w-20 h-20 rounded-full bg-brand-light flex items-center justify-center" data-testid={`payment-${state}`}>{C.icon}</div>
        <h1 className="mt-6 font-heading font-extrabold text-2xl text-brand-purpleDeep">{C.title}</h1>
        <p className="mt-3 text-brand-purpleDeep/60">{C.sub}</p>
        <button onClick={() => navigate("/client")} className="mt-8 inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold px-6 py-3 rounded-lg" data-testid="payment-back">Back to dashboard</button>
      </div>
    </div>
  );
}
